import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { useChildCategoryGrid } from '@/data/Content/ChildCategoryGrid';
import { useSeriesByCatentryId, useSeriesBySeries } from '@/data/Content/Series';
import { ID } from '@/data/types/Basic';
import { CategoryType } from '@/data/types/Category';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import {
	seriesColorSX,
	seriesColorTextSX,
	seriesColorWrapperSingleRowSX,
	seriesColorWrapperSX,
} from 'presentation/components/fiwe/widgets/CategorySeriesProductListWidget/styles/seriesColor';
import {
	seriesDimensionListSX,
	seriesDimensionTextSX,
} from 'presentation/components/fiwe/widgets/CategorySeriesProductListWidget/styles/seriesDimension';
import { FC } from 'react';

export const CategoryMarketingConceptWidget: FC<{ id: ID }> = ({ id }) => {
	// const { category } = useCategory(id);
	const { categories, loading: childrenLoading } = useChildCategoryGrid(id);

	return (
		<Box data-id="CategorySeriesProductList">
			{/* {category?.children?.length ? (
				<Box sx={{ fontSize: '3rem', backgroundColor: 'red' }}>
					{'this is a test of next js and swr'} {category?.children?.length} {id}
				</Box>
			) : null} */}
			{childrenLoading ? (
				<ProgressIndicator />
			) : (
				<Stack>
					{categories?.map((child) => (
						<Stack rowGap={2} key={child.identifier} sx={{ marginBottom: 8 }}>
							<Box>
								<Linkable href={child.seo.href}>
									<Typography variant="h1_underline">{child.name}</Typography>
								</Linkable>
								<Typography variant="body1">{child.shortDescription}</Typography>
							</Box>
							<ColorsGrid category={child} categoryLoading={childrenLoading} />
							<Button
								variant="outlined"
								color="black"
								href={child.seo.href}
								sx={{ alignSelf: 'flex-start' }}
							>
								{'Visa alla'}
							</Button>
						</Stack>
					))}
				</Stack>
			)}
		</Box>
	);
};

// Flexbox variant
const Colors: FC<{
	category: CategoryType;
	categoryLoading: boolean;
}> = ({ category, categoryLoading }) => {
	const { colors, isLoading: seriesLoading } = useSeriesBySeries(category?.name ?? null);

	return (
		<Stack sx={seriesColorWrapperSX}>
			{seriesLoading || categoryLoading ? (
				<Box sx={seriesColorSX({ margin: false })}>
					<Skeleton variant="rectangular" sx={{ height: 'auto', paddingTop: '100%' }} />
					<Skeleton variant="text" />
				</Box>
			) : (
				colors?.map((color) => (
					<Linkable href={color.link} key={color.catentryId} sx={seriesColorSX({ margin: false })}>
						<Img src={color.image} alt={color.name} />
						<Typography sx={seriesColorTextSX}>{color.name}</Typography>
					</Linkable>
				))
			)}
			{[...Array(5)].map((x, i) => (
				<Box sx={seriesColorSX({ margin: false })} key={i}></Box>
			))}
		</Stack>
	);
};

// Grid variant
const ColorsGrid: FC<{
	category: CategoryType;
	categoryLoading: boolean;
}> = ({ category, categoryLoading }) => {
	const {
		colors,
		isLoading: seriesLoading,
		dimensions,
	} = useSeriesBySeries(category?.name ?? null);

	return (
		<Box sx={seriesColorWrapperSingleRowSX}>
			{seriesLoading || categoryLoading
				? [...Array(3)].map((x, i) => (
						<Box sx={seriesColorSX({ margin: false })} key={i}>
							<Skeleton variant="rectangular" sx={{ height: 'auto', paddingTop: '100%' }} />
							<Skeleton variant="text" />
						</Box>
				  ))
				: colors?.map((color) => (
						<Box key={color.catentryId} sx={seriesColorSX({ margin: false })}>
							<Linkable href={color.link} key={color.catentryId}>
								<Img src={color.image} alt={color.name} />
								<Typography sx={seriesColorTextSX}>{color.name}</Typography>
							</Linkable>
							<DimensionList catentryId={color.catentryId} />
						</Box>
				  ))}
		</Box>
	);
};

// List of all dimensions for a series / category
export const DimensionList: FC<{
	catentryId: string;
}> = ({ catentryId }) => {
	const { isLoading, dimensions } = useSeriesByCatentryId(catentryId ?? null);

	return (
		<Box>
			{isLoading ? (
				<Skeleton variant="text" />
			) : (
				<Stack sx={seriesDimensionListSX} direction={'row'} spacing={0}>
					{dimensions?.map((dimension) => (
						<Linkable href={dimension.link} key={dimension.catentryId} sx={seriesDimensionTextSX}>
							{dimension.dimension}
						</Linkable>
					))}
				</Stack>
			)}
		</Box>
	);
};
